import React, { FunctionComponent, useState, ChangeEvent } from 'react';
import some from 'lodash/some';
import { useTranslation } from 'react-i18next';
import { Step1Props } from './types';
import { Step1ValueModel } from '../types';
import { validateStep1 } from '../Validation';
import { validateOrganisationNumber, OrganisationType } from '../../Validation';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import InputText from "@sharedComponents/InputText";
import Alert from "@sharedComponents/Alert";
import { AlertType } from "@sharedComponents/Alert/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";
import { OrgNrValidationStatus } from "@apps/Shared/Services/CompanyRegistrations/interfaces";

const Step1: FunctionComponent<Step1Props> = props => {
    const { t } = useTranslation()

    const [stepValues, setStepValues] = useState<Step1ValueModel>({
        organizationNumber: '',
    })

    const [loading, setLoading] = useState<boolean>(false)

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const [infoMessage, setInfoMessage] = useState<any>(undefined)

    const allErrors = getAllErrors(formErrors)

    const onChangeOrgNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()

        setStepValues({
            ...stepValues,
            organizationNumber: value,
        })

        props.onChangeValues &&
            props.onChangeValues({
                organizationNumber: value,
            })

        const errors: FormErrorType[] = []

        validateOrganisationNumber(
            OrganisationType.Customer,
            value,
            errors,
            false,
            false
        ).then(result => {
            setInfoMessage(result?.info || undefined)
            setFormErrors(result?.errors)
        })
    }

    const nextStep = () => {
        setLoading(true)

        validateStep1(stepValues, formErrors)
            .then(result => {
                const errors: FormErrorType[] = result?.errors
                const info: any = result?.info
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else if (info && !infoMessage) {
                    setInfoMessage(info)
                } else if (
                    result.validationResult === OrgNrValidationStatus.Exists
                ) {
                    setInfoMessage(undefined)
                    setFormErrors([])
                    props.onChangeValues &&
                        props.onChangeValues({
                            requestInfo: false,
                            existingInfo: result?.existingInfo,
                        })
                    props.nextStep && props.nextStep()
                } else {
                    setInfoMessage(undefined)
                    setFormErrors([])

                    props.onChangeValues &&
                        props.onChangeValues({
                            requestInfo: true,
                        })

                    props.nextStep && props.nextStep()
                }
                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
            })
    }

    const onCancel = () => {
        setFormErrors([])

        props.onCancel && props.onCancel()
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }



    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>{props.heading}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>
    
            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="crb-orgnr"
                        label={t('companyRegistration.organisationNumber')}
                        value={stepValues.organizationNumber || ''}
                        required={true}
                        hasError={
                            !!some(formErrors, ['key', 'organizationNumber'])
                        }
                        onChange={onChangeOrgNumber}
                        autoComplete="off"
                        errorText={uniqueErrors('organizationNumber')}
                    />
                </div>
            </div>
            {infoMessage && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type={AlertType.INFO}
                        text={infoMessage.label || ''}
                        subtext={[infoMessage.description]}
                        compact={false}
                    />
                </div>
            )}
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
                            <span>{t('button.next')}</span>
                            <Icon iconName="arrow-right" />
                        </span>
                    }
                    onClick={nextStep}
                    disabled={loading}
                    loading={loading}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step1